import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { BellOutlined, CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import usePushNotifications from "utils/hooks/usePushNotifications";
import axios from "auth/FetchInterceptor";
import { API_BASE_URL } from "configs/AppConfig";

const STORAGE_KEY = "bignova_bo_push_prompt_dismissed";

/**
 * PushNotificationPrompt — BO version.
 * Shows a one-time banner asking authenticated admins to enable push notifications.
 */
const PushNotificationPrompt = () => {
  const { token, userData } = useSelector((state) => state.auth);
  const {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    syncSubscription,
  } = usePushNotifications();
  const [visible, setVisible] = useState(false);

  // Handle silent synchronization on login/account change
  useEffect(() => {
    if (token && isSupported && permission === "granted") {
      syncSubscription();
    }
  }, [token, isSupported, permission, userData?.id, syncSubscription]);

  // Handle silent dissociation on logout
  useEffect(() => {
    if (!token && isSupported && isSubscribed) {
      const handleLogoutUnsync = async () => {
        try {
          
          const registration = await navigator.serviceWorker.ready;
          const pushSubscription =
            await registration.pushManager.getSubscription();
          if (pushSubscription) {
            const endpoint = pushSubscription.endpoint;
            await axios.post(`${API_BASE_URL}/push/unsubscribe`, { endpoint });
            console.log("[PushNotificationPrompt] BO dissociation complete.");
          }
        } catch (err) {
          console.warn(
            "[PushNotificationPrompt] BO dissociation failed:",
            err.message,
          );
        }
      };
      handleLogoutUnsync();
    }
  }, [token, isSupported, isSubscribed]);

  useEffect(() => {
    // Hide if already granted / subscribed
    if (isSubscribed || permission === "granted") {
      setVisible(false);
      return;
    }

    if (!isSupported || !token) return;
    if (permission === "denied") return;

    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;

    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [isSupported, token, isSubscribed, permission]);

  const handleSubscribe = async () => {
    await subscribe();
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        maxWidth: 360,
        width: "calc(100vw - 48px)",
        background: "linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)",
        borderRadius: 16,
        padding: 20,
        boxShadow: "0 8px 32px rgba(109,40,217,0.35)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        animation: "pushSlideUp 0.4s ease",
      }}
    >
      <style>{`
        @keyframes pushSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            background: "rgba(255,255,255,0.15)",
            borderRadius: "50%",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BellOutlined style={{ fontSize: 20, color: "#fff" }} />
        </div>
        <div style={{ flex: 1 }}>
          <p
            style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#fff" }}
          >
            Activer les notifications
          </p>
          <p
            style={{
              margin: 0,
              padding: 0,
              fontSize: 13,
              opacity: 1,
              lineHeight: 1.5,
              color: "rgba(255,255,255,0.9)",
              overflow: "visible",
              whiteSpace: "normal",
            }}
          >
            Recevez les alertes de la plateforme en temps réel.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.7)",
            padding: 4,
            display: "flex",
          }}
          aria-label="Fermer"
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          size="small"
          onClick={handleDismiss}
          style={{
            flex: 1,
            background: "rgba(255,255,255,0.15)",
            border: "none",
            color: "#fff",
            borderRadius: 8,
          }}
        >
          Plus tard
        </Button>
        <Button
          type="primary"
          size="small"
          loading={isLoading}
          onClick={handleSubscribe}
          style={{
            flex: 2,
            background: "#fff",
            color: "#6d28d9",
            fontWeight: 700,
            border: "none",
            borderRadius: 8,
          }}
        >
          Activer
        </Button>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
